// / — the signed-in landing page.
//
// One question: "what's next?". A sticky day-of box, then every other open
// event as a card with inline RSVP.
//
// It used to answer a second question too, "what are my groups up to?", with a
// card per group previewing its feed, and it had a panel mode beside this feed
// mode behind an account-level switch. Both went 2026-10-07 after the first
// live test: the Groups tab answers the second question, and one home is
// easier to learn than two. `site_user_info.home_mode` and `PATCH /me/home-mode`
// are left in place and unused rather than migrated away.
//
// This replaced the slice-0 proof page. That page reported the token round trip
// and nothing else, so a signed-in user landed somewhere with no way forward —
// every real view was reachable only by typing its URL.

import { ApiClientError, fetchActivity, listGroups, listMyEvents } from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { el } from '../dom.js';
import {
  nonDayOfEvents,
  unansweredCount,
  type ActivityItem,
  type GroupSummary,
  type MyEventSummary,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { todayInZone } from './events.js';
import { eventCard } from './eventCard.js';
import { pinnableEvents, stickyPanel } from './stickyPanel.js';
import { myFastStrip } from './myFastStrip.js';
import { getCurrentUserId } from '../chrome.js';
import { postCard } from './feed.js';
import { messageRow } from './chat.js';

export const homeView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let groups: GroupSummary[];
  let events: MyEventSummary[];
  let zone: string;
  try {
    [groups, events, zone] = await Promise.all([
      listGroups(),
      // Open events only: a closed one is not "next up" no matter how soon it is.
      //
      // ⚠ 200, not 50, and 200 is the endpoint's own maximum. The row cap is
      // the LAST place the original defect can still hide: the server orders by
      // date, so a cap cuts the FAR events — which after 2026-09-10 are exactly
      // the ones this page most wants, because a far unanswered invite now sorts
      // to the top of the panel. Caught on a dev database holding 49 open
      // events, where a December invite fell off the end of a 50-row page and
      // the panel could not show what it had never been sent.
      //
      // A cap is still right — this is one page load, not a paginated view —
      // and `/events` carries any tail. But it belongs somewhere no real account
      // reaches, not just above the common case.
      listMyEvents({ upcoming: true, includeClosed: false, limit: 200 }),
      viewerZone(),
    ]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your dashboard.'));
    return;
  }

  // The server's `upcoming` cut is site-local, which is right for cron but can
  // keep an event that is already yesterday for a viewer well ahead of the site
  // zone. Re-cut on the viewer's own today, exactly as the event box does.
  //
  // ⚠ No slice. There used to be a NEXT_UP_LIMIT of 5 here, stacked on the
  // panel's own limit of 5, and the two together were the defect: an invite
  // three months out never reached the panel, not because it was far away but
  // because it was sixth. Ordering by "unanswered first" is what replaces the
  // limit — the tail is now sorted below the things that need an answer instead
  // of being cut off. `listMyEvents` still caps at 50 rows, which is the real
  // backstop.
  const today = todayInZone(zone);
  const upcoming = events.filter((e) => e.eventDate >= today);
  const pinnable = pinnableEvents(upcoming);
  const groupNames = new Map(groups.map((g) => [g.groupId, g.name]));

  const filters = loadFilters();
  const stream = feed(pinnable, zone, groupNames, filters.events);
  const activity = activityStream(zone, filters);
  onLeave(activity.stop);

  // Above the stream and OUT of the day-of box: the box is rsvpControl chrome
  // for events that need an answer, and a fast is not an event and has no RSVP.
  //
  // It fetches itself and starts empty, so it costs this page no latency and a
  // failed fasting read cannot stop the page rendering.
  const fastStrip = myFastStrip();
  onLeave(fastStrip.stop);

  main.replaceChildren(
    el('div', { class: 'page-head' }, [
      el('h1', {}, 'Next up'),
      el('p', { class: 'muted' }, `Times shown in ${zone}`),
    ]),
    fastStrip.root,
    filterChips(filters, (key) => {
      saveFilters(filters);
      if (key === 'events') stream.setEventsVisible(filters.events);
      else activity.reload();
    }),
    ...stream.nodes,
    upcoming.length
      ? el('p', { class: 'small' }, [el('a', { href: '/events' }, 'All events')])
      : noEvents(groups.length > 0),
    activity.root,
  );
};

// --- The filter -----------------------------------------------------------
//
// Three switches over three kinds of thing on one page: event cards, group
// posts, and event chat. ALL ON by default (Will, 2026-10-16). This is the old
// feed/panel toggle's job done without a mode: one home, and the member narrows
// it. Kept per DEVICE in localStorage, not on the account: it is a view
// preference, and `site_user_info.home_mode` stays unused. Every access is
// wrapped, because storage can be absent or throw (private windows, blocked
// site data) and the page must render with everything on when it does.
//
// ⚠ What a filter may NOT hide: the day-of confirmation box and the "needs your
// answer" line. Turning Events off narrows the list of CARDS, never the one
// thing on this page that needs an action.

interface HomeFilters {
  events: boolean;
  posts: boolean;
  chat: boolean;
}

const FILTER_KEY = 'msc.home.filters';

function loadFilters(): HomeFilters {
  const all: HomeFilters = { events: true, posts: true, chat: true };
  try {
    const raw = window.localStorage.getItem(FILTER_KEY);
    if (!raw) return all;
    const parsed = JSON.parse(raw) as Partial<HomeFilters>;
    return {
      events: parsed.events !== false,
      posts: parsed.posts !== false,
      chat: parsed.chat !== false,
    };
  } catch {
    return all;
  }
}

function saveFilters(filters: HomeFilters): void {
  try {
    window.localStorage.setItem(FILTER_KEY, JSON.stringify(filters));
  } catch {
    // Not persisted. The filter still works for this visit.
  }
}

function filterChips(
  filters: HomeFilters,
  onChange: (key: keyof HomeFilters) => void,
): HTMLElement {
  const defs: [keyof HomeFilters, string][] = [
    ['events', 'Events'],
    ['posts', 'Group posts'],
    ['chat', 'Event chat'],
  ];
  const row = el('div', { class: 'home-filters', role: 'group', 'aria-label': 'Show on home' });
  for (const [key, label] of defs) {
    // A real button with aria-pressed, so the state is TEXT to assistive tech
    // and a tap target on a phone, and the checkmark means it is not colour alone.
    const chip = el('button', { type: 'button', class: 'filter-chip' }) as HTMLButtonElement;
    const sync = (): void => {
      chip.textContent = `${filters[key] ? '✓ ' : ''}${label}`;
      chip.setAttribute('aria-pressed', filters[key] ? 'true' : 'false');
      chip.classList.toggle('filter-chip-on', filters[key]);
    };
    chip.addEventListener('click', () => {
      filters[key] = !filters[key];
      sync();
      onChange(key);
    });
    sync();
    row.append(chip);
  }
  return row;
}

// --- The activity stream --------------------------------------------------

/**
 * Group posts and readable event chat from every group, newest first, in FULL.
 *
 * No preview and no truncation (Will, 2026-10-16): the point of this page is to
 * read everything without clicking around. Each item is rendered by the same
 * `postCard()` / `messageRow()` the group feed and the chat thread use, so
 * reactions, comments, delete, report and block all work in place.
 *
 * Not live. The group feed and the chat thread have sockets; this page loads on
 * visit and has a Refresh. Subscribing to every group and event channel here is
 * a bigger decision than this page needs yet.
 */
function activityStream(
  zone: string,
  filters: HomeFilters,
): { root: HTMLElement; reload: () => void; stop: () => void } {
  const list = el('div', { class: 'home-activity-list' });
  const note = el('p', { class: 'muted small', hidden: true });
  const more = el('button', { type: 'button', class: 'btn btn-link', hidden: true }, 'Load older') as HTMLButtonElement;
  const root = el('section', { class: 'home-activity' }, [list, note, more]);

  let cursor: string | null = null;
  // A reload while a fetch is in flight must not let the stale response land
  // after the new one. Each load takes a ticket; only the latest may render.
  let ticket = 0;
  let stopped = false;

  function kinds(): ('posts' | 'chat')[] {
    return [...(filters.posts ? (['posts'] as const) : []), ...(filters.chat ? (['chat'] as const) : [])];
  }

  function render(item: ActivityItem, viewerId: number | null): HTMLElement {
    if (item.kind === 'post') {
      const node = postCard(item.post, zone, viewerId, () => node.remove(), false);
      node.prepend(
        el('p', { class: 'small muted activity-source' }, [
          el('a', { href: `/groups/${item.groupId}` }, item.groupName),
        ]),
      );
      return node;
    }
    const inner = messageRow(item.message, zone, viewerId, () => wrap.remove());
    const wrap = el('article', { class: 'card feed-item activity-chat' }, [
      el('p', { class: 'small muted activity-source' }, [
        '💬 ',
        el('a', { href: `/events/${item.eventId}/chat` }, item.eventTitle),
        ' · ',
        el('a', { href: `/groups/${item.groupId}` }, item.groupName),
      ]),
      inner,
    ]);
    return wrap;
  }

  async function load(reset: boolean): Promise<void> {
    const mine = ++ticket;
    const wanted = kinds();
    if (reset) {
      list.replaceChildren();
      cursor = null;
      more.hidden = true;
    }
    if (wanted.length === 0) {
      note.textContent = 'Group posts and event chat are hidden. Turn one on above to see them.';
      note.hidden = false;
      return;
    }
    note.hidden = true;
    more.setAttribute('disabled', 'disabled');
    try {
      const [page, viewerId] = await Promise.all([
        fetchActivity({ before: reset ? null : cursor, kinds: wanted }),
        getCurrentUserId(),
      ]);
      if (stopped || mine !== ticket) return;
      for (const item of page.items) list.append(render(item, viewerId));
      cursor = page.cursor;
      more.hidden = !page.hasMore;
      if (list.childElementCount === 0) {
        note.textContent = 'No messages yet.';
        note.hidden = false;
      }
    } catch {
      if (stopped || mine !== ticket) return;
      note.textContent = 'Could not load messages.';
      note.hidden = false;
    } finally {
      more.removeAttribute('disabled');
    }
  }

  more.addEventListener('click', () => void load(false));
  void load(true);

  return {
    root,
    reload: () => void load(true),
    stop: () => {
      stopped = true;
    },
  };
}

/**
 * The home stream: a sticky day-of box, then every OTHER open event as a card.
 *
 * No new endpoint and no new card. `listMyEvents` already returns exactly this
 * shape cross-group with the viewer's own status and counts on each row, and
 * `eventCard({ rsvp: true })` already renders it with inline RSVP — which is
 * why the whole mode is a list comprehension rather than a feature.
 *
 * The day-of box is the ONE thing that stays sticky here. A card scrolled past
 * is a card missed, and day-of confirmation is the differentiator. It self-hides
 * when nothing is on today, which is most days, so it costs nothing the rest of
 * the time.
 *
 * ⚠ **The box and the cards PARTITION one list.** Today's event is in the box
 * and NOT repeated below it. Until 2026-09-10 it was in both, which §3e argued
 * for and which was wrong in practice: one event rendered twice, with two RSVP
 * controls and two headcounts that could disagree until a reload, and nothing
 * on screen saying they were the same row. Will called it — it reads as a bug
 * whatever the rationale, and "the box must not be scrollable-past" is fully
 * served by the box alone.
 *
 * That is also what retired the box→card sync this function used to carry: the
 * card it reached for no longer exists, and the reverse direction (answer on a
 * card, box goes stale) was never wired at all — `eventCard` passes no
 * `onChange` to `rsvpControl`. Disjoint lists mean neither direction has
 * anything to sync, which is a better answer than two callbacks kept in step.
 *
 * `now` is read ONCE and handed to both halves. "Today" resolves in the event's
 * AUTHORING zone, so two clocks straddling midnight can put an event in both
 * lists or in neither — one tick, one clock, the rule cron learned twice.
 */
function feed(
  pinnable: MyEventSummary[],
  zone: string,
  groupNames: Map<number, string>,
  eventsVisible: boolean,
): { nodes: HTMLElement[]; setEventsVisible: (on: boolean) => void } {
  const now = Date.now();
  const rest = nonDayOfEvents(pinnable, now);

  const cards = el(
    'div',
    { class: 'home-feed' },
    rest.map((e) =>
      eventCard(e, zone, {
        rsvp: true,
        groupLabel: { groupId: e.groupId, name: groupNames.get(e.groupId) ?? e.groupName },
      }),
    ),
  );

  const dayOf = stickyPanel({
    events: pinnable,
    viewerZone: zone,
    groupNames,
    dayOfOnly: true,
    nowMs: now,
  });

  // Counts the CARDS, not the whole backlog: the day-of box carries its own
  // "Needs your answer" chip for the event it holds, and a number that includes
  // a row rendered nowhere beneath it is the same confusion in a different
  // shape — "2 events need your answer" above a single card.
  const waiting = unansweredCount(rest);
  // `hidden` is only a UA rule and `.home-feed { display: flex }` beats it, so
  // the companion `[hidden]` rule in styles.css is what makes this work.
  cards.hidden = !eventsVisible;
  return {
    nodes: [
      dayOf.root,
      ...(waiting > 0
        ? [
            el(
              'p',
              { class: 'small home-backlog' },
              `${waiting} ${waiting === 1 ? 'event needs' : 'events need'} your answer`,
            ),
          ]
        : []),
      cards,
    ],
    setEventsVisible: (on: boolean) => {
      cards.hidden = !on;
    },
  };
}

function noEvents(hasGroups: boolean): HTMLElement {
  return el(
    'p',
    { class: 'muted' },
    hasGroups
      ? 'No upcoming events in your groups.'
      : ['Nothing upcoming. ', el('a', { href: '/groups' }, 'Join or create a group'), ' to get started.'],
  );
}
